import React, { useState, useContext, useEffect } from 'react';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import Form from '@amzn/awsui-components-console/form';
import FormField from '@amzn/awsui-components-console/form-field';
import Header from '@amzn/awsui-components-console/header';
import Input from '@amzn/awsui-components-console/input';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import { RegionContext } from '../CustomContext';
import { useDrawer } from '../DrawerContext';
import { updateQueueConfig } from './apis';

export default function EditSettingsPanel({ currentConfig }) {
    const [defaultAllocation, setDefaultAllocation] = useState('');
    const [personalAccountCap, setPersonalAccountCap] = useState('');
    const [queueTimeoutDefault, setQueueTimeoutDefault] = useState('');
    const [queueTimeoutMax, setQueueTimeoutMax] = useState('');
    const [maxPending, setMaxPending] = useState('');
    const [loading, setLoading] = useState(false);
    const [formError, setFormError] = useState('');

    const { selectedRegion } = useContext(RegionContext);
    const { closeDrawer, getCallbacks } = useDrawer();

    useEffect(() => {
        setDefaultAllocation(String(currentConfig.default_allocation ?? ''));
        setPersonalAccountCap(String(currentConfig.personal_account_cap ?? ''));
        setQueueTimeoutDefault(String(currentConfig.queue_timeout_default ?? ''));
        setQueueTimeoutMax(String(currentConfig.queue_timeout_max ?? ''));
        setMaxPending(String(currentConfig.max_pending_per_requester ?? ''));
    }, [currentConfig]);

    const handleSubmit = async () => {
        setLoading(true);
        setFormError('');
        try {
            const updates = [
                { key: 'default_allocation', value: parseInt(defaultAllocation, 10) },
                { key: 'personal_account_cap', value: parseInt(personalAccountCap, 10) },
                { key: 'queue_timeout_default', value: parseInt(queueTimeoutDefault, 10) },
                { key: 'queue_timeout_max', value: parseInt(queueTimeoutMax, 10) },
                { key: 'max_pending_per_requester', value: parseInt(maxPending, 10) },
            ];

            for (const { key, value } of updates) {
                if (isNaN(value) || value < 0) {
                    setFormError(`Invalid value for ${key}. Must be a non-negative number.`);
                    setLoading(false);
                    return;
                }
            }

            const timeoutDefault = parseInt(queueTimeoutDefault, 10);
            const timeoutMax = parseInt(queueTimeoutMax, 10);
            if (timeoutDefault > timeoutMax) {
                setFormError('Default queue timeout cannot be greater than maximum queue timeout.');
                setLoading(false);
                return;
            }

            for (const { key, value } of updates) {
                try {
                    await updateQueueConfig(key, value, selectedRegion.value);
                } catch (err) {
                    err._failedKey = key;
                    throw err;
                }
            }

            const { onSuccess } = getCallbacks();
            if (onSuccess) {
                onSuccess();
            }
            closeDrawer();
        } catch (err) {
            const failedKey = err._failedKey || '';
            if (err.response && err.response.data?.error_message) {
                setFormError(err.response.data.error_message);
            } else {
                setFormError(
                    `Failed to save '${failedKey || 'settings'}'. Earlier settings may have been saved. Please verify.`
                );
            }
            // Reload config to show what actually persisted
            const { onSuccess } = getCallbacks();
            if (onSuccess) {
                onSuccess();
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box padding="l">
            <Form
                header={<Header variant="h2">Edit settings</Header>}
                errorText={formError}
                actions={
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link" onClick={closeDrawer}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={handleSubmit} loading={loading}>
                            Save
                        </Button>
                    </SpaceBetween>
                }
            >
                <SpaceBetween size="l">
                    <FormField
                        label="Default allocation per device type"
                        description="Maximum devices of the same type a bot can hold before requests are queued."
                    >
                        <Input
                            value={defaultAllocation}
                            onChange={({ detail }) => setDefaultAllocation(detail.value)}
                            type="number"
                        />
                    </FormField>
                    <FormField
                        label="Personal account allocation"
                        description="Maximum devices of the same type a personal account can hold at the same time."
                    >
                        <Input
                            value={personalAccountCap}
                            onChange={({ detail }) => setPersonalAccountCap(detail.value)}
                            type="number"
                        />
                    </FormField>
                    <FormField
                        label="Default queue timeout (minutes)"
                        description="How long a request waits before expiring when no custom timeout is specified."
                    >
                        <Input
                            value={queueTimeoutDefault}
                            onChange={({ detail }) => setQueueTimeoutDefault(detail.value)}
                            type="number"
                        />
                    </FormField>
                    <FormField
                        label="Maximum queue timeout (minutes)"
                        description="The longest a caller can request to wait. Values above this are capped."
                    >
                        <Input
                            value={queueTimeoutMax}
                            onChange={({ detail }) => setQueueTimeoutMax(detail.value)}
                            type="number"
                        />
                    </FormField>
                    <FormField
                        label="Max pending requests per caller"
                        description="Maximum queued requests per caller. Prevents queue flooding."
                    >
                        <Input
                            value={maxPending}
                            onChange={({ detail }) => setMaxPending(detail.value)}
                            type="number"
                        />
                    </FormField>
                </SpaceBetween>
            </Form>
        </Box>
    );
}
